/**
 * A money amount typed into a text field, in the operator's UI language.
 *
 * Chromium paints an `<input type="number">` in the operating system's number
 * format and honours neither the page's `lang` nor the browser's language, so
 * an English admin on a German machine reads "0,00" in a field it means as
 * "0.00". The fields that take an amount are therefore text fields with
 * `inputmode="decimal"`, written and read here against the UI language.
 */
import type { Locale } from "@momo/i18n";

const DECIMAL: Record<Locale, "," | "."> = { de: ",", en: "." };

/** `major` (euros, not cents) as the field shows it: the UI language's own
 *  decimal mark, exactly `digits` fraction digits, no grouping — a grouped
 *  "1.000" in a field the operator edits reads as one euro in English. */
export function formatAmountInput(
	major: number,
	locale: Locale,
	digits: number,
): string {
	return major.toFixed(digits).replace(".", DECIMAL[locale]);
}

/** What the operator typed, in major units — or null when it is no amount.
 *  The UI language's decimal mark is the decimal, with at most two digits
 *  after it, and the other mark may group the whole euros in threes. A number
 *  carrying only the other mark followed by one or two digits reads that mark
 *  as the decimal (a price pasted from elsewhere). Anything else is refused
 *  rather than guessed: "1.234,50" or "12.345" in English each could
 *  mean two amounts a thousand times apart, and the form shows its error. */
export function parseAmountInput(text: string, locale: Locale): number | null {
	const compact = text.replace(/\s/g, "");
	if (compact === "") return null;
	const own = DECIMAL[locale];
	const group = own === "," ? "." : ",";
	if (
		new RegExp(
			`^(?:\\d{1,3}(?:\\${group}\\d{3})+|\\d+)?(?:\\${own}\\d{1,2})?$`,
		).test(compact)
	)
		return Number(compact.split(group).join("").replace(own, "."));
	if (new RegExp(`^\\d+\\${group}\\d{1,2}$`).test(compact))
		return Number(compact.replace(group, "."));
	return null;
}
